// Monitoring Service - Automatic health checks and incident detection
import {
  createIncident,
  updateIncident,
  addIncidentUpdate,
  getActiveIncidents,
  getServices,
  type Incident,
  type ServiceConfig,
} from './statusService';

export interface HealthCheckConfig {
  serviceId: string;
  name: string;
  endpoint: string;
  method?: 'GET' | 'POST' | 'HEAD';
  timeout?: number; // ms
  interval?: number; // ms
  expectedStatus?: number;
  headers?: Record<string, string>;
}

export interface HealthCheckResult {
  serviceId: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTime: number; // ms
  statusCode?: number;
  error?: string;
  timestamp: string;
}

export interface MonitoringConfig {
  enabled: boolean;
  checkInterval: number; // ms, default 60000 (1 minute)
  degradedThreshold: number; // ms, response time threshold for degraded status
  autoCreateIncidents: boolean;
  autoResolveIncidents: boolean;
  consecutiveFailuresBeforeIncident: number;
  consecutiveSuccessesBeforeResolve: number;
}

// Default health check endpoints (configure these for your actual services)
const defaultHealthChecks: HealthCheckConfig[] = [
  {
    serviceId: 'desktop-app',
    name: 'Ochtopus Desktop App',
    endpoint: 'https://api.ochtopus.com/health/desktop',
    timeout: 10000,
    interval: 60000,
  },
  {
    serviceId: 'ai-agent',
    name: 'AI Agent & Autocomplete',
    endpoint: 'https://api.ochtopus.com/health/ai',
    timeout: 15000,
    interval: 30000,
  },
  {
    serviceId: 'api',
    name: 'Ochtopus API',
    endpoint: 'https://api.ochtopus.com/health',
    timeout: 5000,
    interval: 30000,
  },
  {
    serviceId: 'marketplace',
    name: 'Extensions Marketplace',
    endpoint: 'https://marketplace.ochtopus.com/health',
    timeout: 10000,
    interval: 60000,
  },
  {
    serviceId: 'settings-sync',
    name: 'Settings Sync',
    endpoint: 'https://sync.ochtopus.com/health',
    timeout: 10000,
    interval: 60000,
  },
  {
    serviceId: 'auth',
    name: 'Authentication Services',
    endpoint: 'https://auth.ochtopus.com/health',
    timeout: 5000,
    interval: 30000,
  },
];

// Demo mode endpoints (always return success for demo)
const demoHealthChecks: HealthCheckConfig[] = [
  {
    serviceId: 'desktop-app',
    name: 'Ochtopus Desktop App',
    endpoint: 'https://httpstat.us/200',
    timeout: 10000,
  },
  {
    serviceId: 'ai-agent',
    name: 'AI Agent & Autocomplete',
    endpoint: 'https://httpstat.us/200',
    timeout: 15000,
  },
  {
    serviceId: 'api',
    name: 'Ochtopus API',
    endpoint: 'https://httpstat.us/200',
    timeout: 5000,
  },
  {
    serviceId: 'marketplace',
    name: 'Extensions Marketplace',
    endpoint: 'https://httpstat.us/200',
    timeout: 10000,
  },
  {
    serviceId: 'settings-sync',
    name: 'Settings Sync',
    endpoint: 'https://httpstat.us/200',
    timeout: 10000,
  },
  {
    serviceId: 'auth',
    name: 'Authentication Services',
    endpoint: 'https://httpstat.us/200',
    timeout: 5000,
  },
];

const MONITORING_CONFIG_KEY = 'ochtopus_monitoring_config';
const MONITORING_STATE_KEY = 'ochtopus_monitoring_state';
const API_KEYS_KEY = 'ochtopus_monitoring_api_keys';

export interface ExternalApiKeys {
  uptimeRobot?: string;
  betterUptime?: string;
  pingdom?: string;
}

// Get API keys
export function getApiKeys(): ExternalApiKeys {
  if (typeof window === 'undefined') return {};
  const stored = localStorage.getItem(API_KEYS_KEY);
  if (!stored) return {};
  try {
    return JSON.parse(stored);
  } catch {
    return {};
  }
}

// Save API keys
export function saveApiKeys(keys: ExternalApiKeys): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(API_KEYS_KEY, JSON.stringify(keys));
}

interface MonitoringState {
  lastCheck: Record<string, string>; // serviceId -> ISO timestamp
  failureCount: Record<string, number>; // serviceId -> consecutive failures
  successCount: Record<string, number>; // serviceId -> consecutive successes
  activeIncidentIds: Record<string, string>; // serviceId -> incident id
  responseHistory: Record<string, HealthCheckResult[]>; // serviceId -> last N results
}

// Get monitoring configuration
export function getMonitoringConfig(): MonitoringConfig {
  if (typeof window === 'undefined') {
    return getDefaultConfig();
  }

  const stored = localStorage.getItem(MONITORING_CONFIG_KEY);
  if (!stored) {
    return getDefaultConfig();
  }

  try {
    return JSON.parse(stored);
  } catch {
    return getDefaultConfig();
  }
}

function getDefaultConfig(): MonitoringConfig {
  return {
    enabled: false, // Disabled by default, enable in admin
    checkInterval: 60000, // 1 minute
    degradedThreshold: 3000, // 3 seconds
    autoCreateIncidents: true,
    autoResolveIncidents: true,
    consecutiveFailuresBeforeIncident: 3,
    consecutiveSuccessesBeforeResolve: 2,
  };
}

// Save monitoring configuration
export function saveMonitoringConfig(config: MonitoringConfig): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MONITORING_CONFIG_KEY, JSON.stringify(config));
}

// Get monitoring state
function getMonitoringState(): MonitoringState {
  if (typeof window === 'undefined') {
    return getDefaultState();
  }

  const stored = localStorage.getItem(MONITORING_STATE_KEY);
  if (!stored) {
    return getDefaultState();
  }

  try {
    return JSON.parse(stored);
  } catch {
    return getDefaultState();
  }
}

function getDefaultState(): MonitoringState {
  return {
    lastCheck: {},
    failureCount: {},
    successCount: {},
    activeIncidentIds: {},
    responseHistory: {},
  };
}

// Save monitoring state
function saveMonitoringState(state: MonitoringState): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MONITORING_STATE_KEY, JSON.stringify(state));
}

// Get health check configurations
export function getHealthChecks(demoMode = true): HealthCheckConfig[] {
  // Use demo mode by default since real endpoints don't exist yet
  return demoMode ? demoHealthChecks : defaultHealthChecks;
}

// Perform a single health check
export async function checkHealth(config: HealthCheckConfig): Promise<HealthCheckResult> {
  const startTime = performance.now();
  const timestamp = new Date().toISOString();

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout || 10000);

    const response = await fetch(config.endpoint, {
      method: config.method || 'GET',
      headers: config.headers,
      signal: controller.signal,
      mode: 'cors',
    });

    clearTimeout(timeoutId);
    const responseTime = Math.round(performance.now() - startTime);

    const expectedStatus = config.expectedStatus || 200;
    const isHealthy = response.status === expectedStatus;

    // Check if response time indicates degraded performance
    const monitoringConfig = getMonitoringConfig();
    const isDegraded = responseTime > monitoringConfig.degradedThreshold;

    return {
      serviceId: config.serviceId,
      status: isHealthy ? (isDegraded ? 'degraded' : 'healthy') : 'down',
      responseTime,
      statusCode: response.status,
      timestamp,
    };
  } catch (error) {
    const responseTime = Math.round(performance.now() - startTime);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    return {
      serviceId: config.serviceId,
      status: 'down',
      responseTime,
      error: errorMessage,
      timestamp,
    };
  }
}

// Check all services
export async function checkAllServices(demoMode = true): Promise<HealthCheckResult[]> {
  const healthChecks = getHealthChecks(demoMode);
  const results = await Promise.all(healthChecks.map(config => checkHealth(config)));
  return results;
}

// Process health check results and create/resolve incidents automatically
export async function processHealthCheckResults(results: HealthCheckResult[]): Promise<void> {
  const config = getMonitoringConfig();
  if (!config.enabled) return;

  const state = getMonitoringState();
  const services = getServices();

  for (const result of results) {
    const service = services.find(s => s.id === result.serviceId);
    if (!service) continue;

    // Update response history
    if (!state.responseHistory[result.serviceId]) {
      state.responseHistory[result.serviceId] = [];
    }
    state.responseHistory[result.serviceId].unshift(result);
    state.responseHistory[result.serviceId] = state.responseHistory[result.serviceId].slice(0, 100);

    // Update last check time
    state.lastCheck[result.serviceId] = result.timestamp;

    if (result.status === 'down') {
      // Increment failure count, reset success count
      state.failureCount[result.serviceId] = (state.failureCount[result.serviceId] || 0) + 1;
      state.successCount[result.serviceId] = 0;

      // Create incident if threshold reached and no active incident
      if (
        config.autoCreateIncidents &&
        state.failureCount[result.serviceId] >= config.consecutiveFailuresBeforeIncident &&
        !state.activeIncidentIds[result.serviceId]
      ) {
        const incident = createIncident({
          title: `${service.name} - Service Unavailable`,
          status: 'investigating',
          impact: 'major',
          affectedServices: [result.serviceId],
          createdAt: new Date().toISOString(),
          updates: [{
            id: `upd-${Date.now()}`,
            status: 'Investigating',
            message: `Automated monitoring detected that ${service.name} is not responding. Error: ${result.error || 'Service unreachable'}`,
            timestamp: new Date().toISOString(),
          }],
        });
        state.activeIncidentIds[result.serviceId] = incident.id;
      }
    } else if (result.status === 'degraded') {
      // Handle degraded performance
      state.failureCount[result.serviceId] = (state.failureCount[result.serviceId] || 0) + 1;
      state.successCount[result.serviceId] = 0;

      if (
        config.autoCreateIncidents &&
        state.failureCount[result.serviceId] >= config.consecutiveFailuresBeforeIncident &&
        !state.activeIncidentIds[result.serviceId]
      ) {
        const incident = createIncident({
          title: `${service.name} - Degraded Performance`,
          status: 'investigating',
          impact: 'minor',
          affectedServices: [result.serviceId],
          createdAt: new Date().toISOString(),
          updates: [{
            id: `upd-${Date.now()}`,
            status: 'Investigating',
            message: `Automated monitoring detected degraded performance for ${service.name}. Response time: ${result.responseTime}ms`,
            timestamp: new Date().toISOString(),
          }],
        });
        state.activeIncidentIds[result.serviceId] = incident.id;
      }
    } else {
      // Service is healthy
      state.successCount[result.serviceId] = (state.successCount[result.serviceId] || 0) + 1;
      state.failureCount[result.serviceId] = 0;

      // Auto-resolve incident if threshold reached
      if (
        config.autoResolveIncidents &&
        state.successCount[result.serviceId] >= config.consecutiveSuccessesBeforeResolve &&
        state.activeIncidentIds[result.serviceId]
      ) {
        const incidentId = state.activeIncidentIds[result.serviceId];
        addIncidentUpdate(incidentId, {
          status: 'Resolved',
          message: `Automated monitoring confirmed ${service.name} is now responding normally. Response time: ${result.responseTime}ms`,
          timestamp: new Date().toISOString(),
        });
        delete state.activeIncidentIds[result.serviceId];
      }
    }
  }

  saveMonitoringState(state);
}

// Get response history for a service
export function getResponseHistory(serviceId: string): HealthCheckResult[] {
  const state = getMonitoringState();
  return state.responseHistory[serviceId] || [];
}

// Get average response time for a service (last N checks)
export function getAverageResponseTime(serviceId: string, count = 10): number {
  const history = getResponseHistory(serviceId).slice(0, count);
  if (history.length === 0) return 0;

  const sum = history.reduce((acc, r) => acc + r.responseTime, 0);
  return Math.round(sum / history.length);
}

// Get uptime percentage for a service (last N checks)
export function getRecentUptime(serviceId: string, count = 100): number {
  const history = getResponseHistory(serviceId).slice(0, count);
  if (history.length === 0) return 100;

  const healthy = history.filter(r => r.status === 'healthy').length;
  return Math.round((healthy / history.length) * 100);
}

// Monitoring loop manager
let monitoringInterval: NodeJS.Timeout | null = null;

export function startMonitoring(demoMode = true): void {
  const config = getMonitoringConfig();
  if (!config.enabled) return;

  // Clear existing interval
  if (monitoringInterval) {
    clearInterval(monitoringInterval);
  }

  // Run immediately
  runHealthChecks(demoMode);

  // Set up interval
  monitoringInterval = setInterval(() => {
    runHealthChecks(demoMode);
  }, config.checkInterval);

  console.log(`[Monitoring] Started with ${config.checkInterval}ms interval`);
}

export function stopMonitoring(): void {
  if (monitoringInterval) {
    clearInterval(monitoringInterval);
    monitoringInterval = null;
    console.log('[Monitoring] Stopped');
  }
}

async function runHealthChecks(demoMode: boolean): Promise<void> {
  console.log('[Monitoring] Running health checks...');
  const results = await checkAllServices(demoMode);
  await processHealthCheckResults(results);
  console.log('[Monitoring] Health checks completed', results);
}

// Manual health check trigger
export async function triggerHealthCheck(demoMode = true): Promise<HealthCheckResult[]> {
  const results = await checkAllServices(demoMode);
  await processHealthCheckResults(results);
  return results;
}

// Reset monitoring state
export function resetMonitoringState(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(MONITORING_STATE_KEY);
}

// External monitoring integrations
export interface UptimeRobotMonitor {
  id: number;
  friendly_name: string;
  status: number; // 0=paused, 1=not checked, 2=up, 8=seems down, 9=down
  url: string;
}

// Fetch status from UptimeRobot (requires API key)
export async function fetchUptimeRobotStatus(apiKey: string): Promise<UptimeRobotMonitor[]> {
  try {
    const response = await fetch('https://api.uptimerobot.com/v2/getMonitors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        api_key: apiKey,
        format: 'json',
      }),
    });

    const data = await response.json();
    return data.monitors || [];
  } catch (error) {
    console.error('Failed to fetch UptimeRobot status:', error);
    return [];
  }
}

// Better Uptime integration
export interface BetterUptimeMonitor {
  id: string;
  attributes: {
    url: string;
    pronounceable_name: string;
    status: 'up' | 'down' | 'paused' | 'pending' | 'maintenance';
  };
}

export async function fetchBetterUptimeStatus(apiToken: string): Promise<BetterUptimeMonitor[]> {
  try {
    const response = await fetch('https://betteruptime.com/api/v2/monitors', {
      headers: {
        'Authorization': `Bearer ${apiToken}`,
      },
    });

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Failed to fetch Better Uptime status:', error);
    return [];
  }
}
